import { ReactNode, createContext, useContext, useMemo } from 'react';

/**
 * Router-capable path prefixes for modules that can be navigated to
 * via React Router without a full page reload.
 *
 * This is the single source of truth — update here when new module routes are added.
 */
const MODULE_ROUTER_PREFIXES: readonly string[] = [
  '/modules/gym',
  '/modules/vif',
  '/modules/wdyk',
  '/modules/wdyk2',
  '/modules/word-problems',
  '/modules/games/fraction-wheels',
  '/modules/games/k2-undo',
];

interface NavigationContextValue {
  routerPrefixes: readonly string[];
  /** Replacement for `/modules/` in hub preview hrefs; the hub default applies when omitted. */
  previewModulesPath?: string;
}

const NavigationContext = createContext<NavigationContextValue>({
  routerPrefixes: MODULE_ROUTER_PREFIXES,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationContext(): NavigationContextValue {
  return useContext(NavigationContext);
}

/**
 * Overrides navigation routing behaviour for the subtree.
 *
 * Pass `routerPrefixes={[]}` to force all navigation to use
 * `window.location.href` — e.g. in the classic environment where React Router
 * cannot handle cross-module navigation.
 *
 * When no `NavigationProvider` is present, the default prefixes apply and
 * `useModuleNavigation` behaves as normal.
 *
 * Pass `previewModulesPath` to change how the hub rewrites module hrefs in
 * preview mode (e.g. `/modules/preview/` for the classic site).
 */
export function NavigationProvider({
  children,
  routerPrefixes,
  previewModulesPath,
}: {
  children: ReactNode;
  routerPrefixes: readonly string[];
  previewModulesPath?: string;
}) {
  const value = useMemo(
    () => ({ routerPrefixes, previewModulesPath }),
    [routerPrefixes, previewModulesPath],
  );
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}
